import type { CalendarEventView } from "@portal/schemas";

/** 日本時間での 1 日(分単位)。0〜1440。 */
export const DAY_MINUTES = 24 * 60;
const JST_OFFSET = "+09:00";

/** 表示する予定 1 件の位置。時間は「その日の 0 時(日本時間)からの分」。 */
export interface PlacedEvent {
  event: CalendarEventView;
  startMin: number;
  endMin: number;
  /** 重なる予定の中で何列目か(0 始まり)。 */
  column: number;
  /** 重なる予定のかたまり全体の列数。幅は 1/columns。 */
  columns: number;
}

/**
 * その日(YYYY-MM-DD、日本時間)に掛かる予定。週の予定から 1 日分を取り出す(2026-10-14)。
 * 日をまたぐ予定・数日続く終日の予定は、掛かる日すべてに出す。長さ 0 の予定は、始まる日に出す。
 */
export function eventsOnDay(
  date: string,
  events: readonly CalendarEventView[],
): CalendarEventView[] {
  const from = new Date(`${date}T00:00:00${JST_OFFSET}`).getTime();
  const to = from + DAY_MINUTES * 60_000;
  return events.filter((e) => {
    const start = new Date(e.start).getTime();
    const end = new Date(e.end).getTime();
    return start < to && (end > from || (end === start && start >= from));
  });
}

/** 日付(YYYY-MM-DD、日本時間)の 0 時からの経過分。範囲外は 0〜1440 に収める。 */
export function minutesFromDayStart(date: string, iso: string): number {
  const dayStart = new Date(`${date}T00:00:00${JST_OFFSET}`).getTime();
  const min = (new Date(iso).getTime() - dayStart) / 60_000;
  return Math.min(DAY_MINUTES, Math.max(0, min));
}

/**
 * 時刻のある予定を、時間軸に置く位置にする(Google カレンダーの 1 日表示と同じ考え方)。
 * 時間が重なる予定は横に並べる。重なりが連鎖するものを 1 つのかたまりにして、
 * かたまりの中で空いている最初の列に置く。終了と開始がちょうど同じ予定は重ならない。
 */
export function placeEvents(date: string, events: readonly CalendarEventView[]): PlacedEvent[] {
  const items = events
    // 終日の予定は帯に、勤務場所は印とバーに出すので、時間軸のブロックにはしない
    .filter((e) => !e.allDay && !e.workingLocation)
    .map((event) => {
      const startMin = minutesFromDayStart(date, event.start);
      // 0 分の予定も見えるよう、最低 15 分の長さとして置く
      const endMin = Math.max(minutesFromDayStart(date, event.end), startMin + 15);
      return { event, startMin, endMin: Math.min(endMin, DAY_MINUTES) };
    })
    // 日付の外(その日に掛からない)ものは除く
    .filter((i) => i.endMin > i.startMin)
    .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

  const placed: PlacedEvent[] = [];
  let cluster: PlacedEvent[] = [];
  let clusterEnd = -1;
  const columnEnds: number[] = []; // 各列の最後の予定の終了

  const flush = () => {
    const columns = columnEnds.length;
    for (const p of cluster) p.columns = columns;
    placed.push(...cluster);
    cluster = [];
    columnEnds.length = 0;
    clusterEnd = -1;
  };

  for (const item of items) {
    if (cluster.length > 0 && item.startMin >= clusterEnd) flush();
    let column = columnEnds.findIndex((end) => end <= item.startMin);
    if (column === -1) column = columnEnds.length;
    columnEnds[column] = item.endMin;
    cluster.push({ ...item, column, columns: 1 });
    clusterEnd = Math.max(clusterEnd, item.endMin);
  }
  flush();
  return placed;
}

/** 勤務場所(Google カレンダーの「勤務場所」)。時間帯に沿った印とバーで示す。 */
export interface PlacedWorkingLocation {
  event: CalendarEventView;
  /** 終日の勤務場所は null(表示する範囲いっぱいに引く)。 */
  startMin: number | null;
  endMin: number | null;
}

export function placeWorkingLocations(
  date: string,
  events: readonly CalendarEventView[],
): PlacedWorkingLocation[] {
  return events
    .filter((e) => e.workingLocation)
    .flatMap((event): PlacedWorkingLocation[] => {
      if (event.allDay) return [{ event, startMin: null, endMin: null }];
      const startMin = minutesFromDayStart(date, event.start);
      const endMin = minutesFromDayStart(date, event.end);
      return endMin > startMin ? [{ event, startMin, endMin }] : [];
    })
    .sort((a, b) => (a.startMin ?? -1) - (b.startMin ?? -1));
}

/**
 * 時間軸に出す範囲(時)。既定は 8〜19 時で、それより外の予定があれば広げる。
 * 戻り値の end は含まない(19 なら 19:00 まで)。
 */
export function visibleHours(
  placed: readonly { startMin: number | null; endMin: number | null }[],
  base = { start: 8, end: 19 },
): { start: number; end: number } {
  let start = base.start;
  let end = base.end;
  for (const p of placed) {
    if (p.startMin === null || p.endMin === null) continue; // 終日は範囲を広げない
    start = Math.min(start, Math.floor(p.startMin / 60));
    end = Math.max(end, Math.ceil(p.endMin / 60));
  }
  return { start: Math.max(0, start), end: Math.min(24, end) };
}
